import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { City, Country, Customer, State } from '../../../modals/product.modals';
import { ClientSettingsService } from '../../../services/client-settings.service';
import { PricingGroup } from '../../../services/offer-group.service';
import { ProductDbService } from '../../../services/productdb.service';

@Component({
  selector: 'app-customer-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    InputTextModule,
    DropdownModule,
    ButtonModule,
    ToastModule,
  ],
  templateUrl: './customer-form.component.html',
  styleUrls: ['./customer-form.component.scss'],
  providers: [ProductDbService, MessageService],
})
export class CustomerFormComponent implements OnInit {
  @Input() visible: boolean = false;
  @Input() mode: 'add' | 'edit' = 'add';
  @Input() customerData: Customer | null = null; // Full customer object for edit mode
  @Input() pricingGroups: PricingGroup[] = [];

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onSave = new EventEmitter<any>();
  @Output() onCancel = new EventEmitter<void>();

  @ViewChild('customerNameInput') customerNameInput!: ElementRef;

  customerForm!: FormGroup;
  isSubmitting: boolean = false;

  // Dropdown data
  countries: Country[] = [];
  states: State[] = [];
  cities: City[] = [];

  // Loading states
  loadingCountries: boolean = false;
  loadingStates: boolean = false;
  loadingCities: boolean = false;
  isContactEnabled: boolean = false;
  constructor(
    private fb: FormBuilder,
    private productService: ProductDbService,
    private messageService: MessageService,
    private clientSettingsService: ClientSettingsService,
  ) {}

  ngOnInit() {
    this.isContactEnabled = this.clientSettingsService.isContactEnabled();
    this.initializeForm();
    this.loadCountries();
  }

  ngOnChanges() {
    if (this.visible && this.customerForm) {
      this.populateForm();
      setTimeout(() => {
        if (this.customerNameInput) {
          this.customerNameInput.nativeElement.focus();
        }
      }, 100);
    }
  }

  initializeForm() {
    this.customerForm = this.fb.group({
      // Company Information
      customer_name: ['', [Validators.pattern(/^[a-zA-Z0-9 ]*$/)]],
      mobile_no: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
      sellingpricegroup_id: [null],
      pan_no: ['', [Validators.pattern(/^[a-zA-Z0-9]*$/)]],
      gst_no: ['', [Validators.pattern(/^[a-zA-Z0-9]*$/)]],

      // Contact Information
      contact_person: ['', [Validators.pattern(/^[a-zA-Z0-9 ]*$/)]],
      address: [''],
      address2: [''],
      address3: [''],
      city_id: [null],
      zip_code: ['', [Validators.pattern(/^[a-zA-Z0-9\-]*$/)]],
      state_id: [null],
      country_id: [null],
      fax_no: ['', [Validators.pattern(/^[0-9\-\+\s]*$/)]],
      email: ['', [Validators.email]],
    });

    // Subscribe to country changes
    this.customerForm.get('country_id')?.valueChanges.subscribe((countryId) => {
      this.onCountryChange(countryId);
    });

    // Subscribe to state changes
    this.customerForm.get('state_id')?.valueChanges.subscribe((stateId) => {
      this.onStateChange(stateId);
    });
  }

  populateForm() {
    if (this.mode === 'edit' && this.customerData) {
      // For edit mode, we need to handle the cascading properly
      // First set country, then load states, then set state, then load cities, then set city
      let countryId = this.customerData.country_id || null;
      let stateId = this.customerData.state_id || null;
      let cityId = this.customerData.city_id || null;

      const countryName = this.customerData.country;
      const stateName = this.customerData.state;
      const cityName = this.customerData.city;

      this.customerForm.patchValue({
        customer_name: this.customerData.customer_name || '',
        mobile_no: this.customerData.mobile_no || '',
        sellingpricegroup_id: this.customerData.sellingpricegroup_id || null,
        pan_no: this.customerData.pan_no || '',
        gst_no: this.customerData.gst_no || '',
        contact_person: this.customerData.contact_person || '',
        address: this.customerData.address_line1 || '',
        address2: this.customerData.address_line2 || '',
        address3: this.customerData.address_line3 || '',
        zip_code: this.customerData.zip_code || '',
        fax_no: this.customerData.fax_no || '',
        email: this.customerData.email || '',
      });

      if (countryId || countryName) {
        if (!countryId && countryName) {
          const matchedCountry = this.countries.find(
            (c) => c.name.toLowerCase() === countryName.toLowerCase(),
          );
          if (matchedCountry) {
            countryId = matchedCountry.id;
          }
        }

        if (countryId) {
          this.customerForm.patchValue(
            { country_id: countryId },
            { emitEvent: false },
          );
          this.loadStates(countryId).then(() => {
            if (!stateId && stateName) {
              const matchedState = this.states.find(
                (s) => s.name.toLowerCase() === stateName.toLowerCase(),
              );
              if (matchedState) {
                stateId = matchedState.id;
              }
            }

            if (stateId) {
              this.customerForm.patchValue(
                { state_id: stateId },
                { emitEvent: false },
              );
              this.loadCities(stateId).then(() => {
                if (!cityId && cityName) {
                  const matchedCity = this.cities.find(
                    (c) => c.name.toLowerCase() === cityName.toLowerCase(),
                  );
                  if (matchedCity) {
                    cityId = matchedCity.id;
                  }
                }

                if (cityId) {
                  this.customerForm.patchValue(
                    { city_id: cityId },
                    { emitEvent: false },
                  );
                }
              });
            }
          });
        }
      }
    } else {
      this.customerForm.reset({
        customer_name: '',
        mobile_no: '',
        sellingpricegroup_id: null,
        pan_no: '',
        gst_no: '',
        country_id: null,
        state_id: null,
        city_id: null,
      });
      this.states = [];
      this.cities = [];
    }
  }

  onDialogHide() {
    this.visible = false;
    this.visibleChange.emit(false);
    this.onCancel.emit();
  }

  onSubmit() {
    if (this.customerForm.invalid) {
      this.markFormGroupTouched(this.customerForm);
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill all mandatory fields',
        life: 3000,
      });
      return;
    }

    this.isSubmitting = true;
    const formData = this.customerForm.value;
    formData.customer_name =
      formData.customer_name != ''
        ? formData.customer_name
        : 'Walk in Customer';
    const enrichedFormData = {
      ...formData,
      state: formData.state_id
        ? this.states.find((s) => s.id === formData.state_id)?.name || ''
        : '',
      country: formData.country_id
        ? this.countries.find((c) => c.id === formData.country_id)?.name || ''
        : '',
      city: formData.city_id
        ? this.cities.find((c) => c.id === formData.city_id)?.name || ''
        : '',
    };

    const apiCall =
      this.mode === 'add'
        ? this.productService.createCustomer(enrichedFormData)
        : this.productService.updateCustomer(
            this.customerData!.id,
            enrichedFormData,
          );

    apiCall.subscribe({
      next: (response: any) => {
        this.isSubmitting = false;

        const isSuccess =
          response.status === 200 ||
          response.status === 201 ||
          response.code === '200' ||
          response.code === 200 ||
          response.code === '201' ||
          response.code === 201;

        if (isSuccess) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail:
              this.mode === 'add'
                ? 'Customer added successfully'
                : 'Customer updated successfully',
            life: 3000,
          });

          this.onSave.emit({
            id: this.customerData?.id || response.data?.id,
            customer_name: formData.customer_name,
            mobile_no: formData.mobile_no,
            sellingpricegroup_id: formData.sellingpricegroup_id,
            ...formData,
          });

          this.visible = false;
          this.visibleChange.emit(false);
        }
      },
      error: (error: any) => {
        this.isSubmitting = false;
        let errorMessage = 'Failed to save customer';

        if (error.status === 400 && error.error?.message) {
          errorMessage = error.error.message;
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage,
          life: 5000,
        });
      },
    });
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  allowOnlyNumbers(event: KeyboardEvent): void {
    const charCode = event.key;
    // Allow only numbers (0-9) and control keys (Backspace, Tab, Delete, Arrow keys)
    if (
      !/^[0-9]$/.test(charCode) &&
      !['Backspace', 'Tab', 'Delete', 'ArrowLeft', 'ArrowRight'].includes(
        event.key,
      )
    ) {
      event.preventDefault();
    }
  }

  sanitizeMobileNumber(event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitized = input.value.replace(/[^0-9]/g, '');
    if (input.value !== sanitized) {
      this.customerForm.patchValue({ mobile_no: sanitized });
    }
  }

  get dialogHeader(): string {
    return this.mode === 'add' ? 'Add Customer' : 'Edit Customer';
  }

  // Load countries on component init
  loadCountries(): void {
    this.loadingCountries = true;
    this.productService.getCountries().subscribe({
      next: (response) => {
        if (response.status === 200 && response.data) {
          this.countries = response.data;
        }
        this.loadingCountries = false;
      },
      error: (error) => {
        console.error('Error loading countries:', error);
        this.loadingCountries = false;
      },
    });
  }

  // Load states when country is selected
  loadStates(countryId: number): Promise<void> {
    return new Promise((resolve) => {
      if (!countryId) {
        this.states = [];
        this.cities = [];
        resolve();
        return;
      }

      this.loadingStates = true;
      this.productService.getStates(countryId).subscribe({
        next: (response) => {
          if (response.status === 200 && response.data) {
            this.states = response.data;
          }
          this.loadingStates = false;
          resolve();
        },
        error: (error) => {
          console.error('Error loading states:', error);
          this.loadingStates = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load states',
            life: 3000,
          });
          resolve();
        },
      });
    });
  }

  // Load cities when state is selected
  loadCities(stateId: number): Promise<void> {
    return new Promise((resolve) => {
      if (!stateId) {
        this.cities = [];
        resolve();
        return;
      }

      this.loadingCities = true;
      this.productService.getCities(stateId).subscribe({
        next: (response) => {
          if (response.status === 200 && response.data) {
            this.cities = response.data;
          }
          this.loadingCities = false;
          resolve();
        },
        error: (error) => {
          console.error('Error loading cities:', error);
          this.loadingCities = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load cities',
            life: 3000,
          });
          resolve();
        },
      });
    });
  }

  // Handle country change
  onCountryChange(countryId: number | null): void {
    // Reset state and city when country changes
    this.customerForm.patchValue(
      {
        state_id: null,
        city_id: null,
      },
      { emitEvent: false },
    );
    this.states = [];
    this.cities = [];

    if (countryId) {
      this.loadStates(countryId);
    }
  }

  // Handle state change
  onStateChange(stateId: number | null): void {
    // Reset city when state changes
    this.customerForm.patchValue(
      {
        city_id: null,
      },
      { emitEvent: false },
    );
    this.cities = [];

    if (stateId) {
      this.loadCities(stateId);
    }
  }
}
